// src/pages/Caja.js
//
// Operación del turno de caja.
//
// Estados:
//   - Sin turno activo → card para abrir.
//   - Con turno abierto → dashboard con resumen por método de pago,
//     movimientos, botón registrar movimiento, botón cerrar turno.
//   - Turno colgado de día anterior → banner rojo forzando cierre.
//
// Requiere autorización de supervisor (badge) para movimientos SALIDA.
//
// Consume el heredado /api/pos/caja/*.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Typography, Stack, Button, IconButton, Chip, Alert, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TextField, MenuItem, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment, Grid, Divider, Paper, LinearProgress,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LockOpenIcon             from '@mui/icons-material/LockOpen';
import LockIcon                 from '@mui/icons-material/Lock';
import AddIcon                  from '@mui/icons-material/Add';
import RemoveIcon               from '@mui/icons-material/Remove';
import WarningIcon              from '@mui/icons-material/Warning';
import RefreshIcon              from '@mui/icons-material/Refresh';
import HistoryIcon              from '@mui/icons-material/History';
import ReceiptLongIcon          from '@mui/icons-material/ReceiptLong';
import { useApp } from '../context/AppContext';
import { caja as apiCaja } from '../api/posApi';

const fmtCOP = (n) => (Number(n) || 0).toLocaleString('es-CO',
  { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
const fmtFechaHora = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('es-CO',
      { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return s; }
};
const esDiaAnterior = (fechaISO) => {
  if (!fechaISO) return false;
  const f = new Date(fechaISO);
  const hoy = new Date();
  return f.toDateString() !== hoy.toDateString();
};

const Caja = () => {
  const { empresa, sucursalActivaId, notify } = useApp();
  const empresaId = empresa?.empresaId || empresa?.EmpresaId;

  const [cajas, setCajas] = useState([]);
  const [metodos, setMetodos] = useState([]);
  const [cajaSeleccionadaId, setCajaSeleccionadaId] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [tick, setTick] = useState(0);   // para forzar recargas

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setCargando(true);
    try {
      const [c, m] = await Promise.all([
        apiCaja.listarCajas(empresaId, sucursalActivaId),
        apiCaja.listarMetodosPago(empresaId),
      ]);
      const arr = Array.isArray(c) ? c : [];
      setCajas(arr);
      setMetodos(Array.isArray(m) ? m : []);
      // Auto-seleccionar: primera con turno activo, si no hay, la primera
      if (!cajaSeleccionadaId && arr.length > 0) {
        const abierta = arr.find(x => x.aperturaActiva);
        setCajaSeleccionadaId(abierta?.cajaId || arr[0].cajaId);
      }
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo cargar.', 'error');
    } finally { setCargando(false); }
  }, [empresaId, sucursalActivaId, notify, cajaSeleccionadaId]);

  useEffect(() => { cargar(); }, [cargar, tick]);

  const cajaSel = useMemo(() =>
    cajas.find(c => c.cajaId === cajaSeleccionadaId), [cajas, cajaSeleccionadaId]);
  const apertura = cajaSel?.aperturaActiva;
  const turnoColgado = apertura && esDiaAnterior(apertura.fechaApertura);

  const forzarRecarga = () => setTick(t => t + 1);

  if (cargando && cajas.length === 0) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  if (cajas.length === 0) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No tienes cajas registradas. Ve a <b>Cajas → Nueva caja</b> para crear al menos una
          antes de poder operar el turno.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <AccountBalanceWalletIcon color="primary" fontSize="large" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={700}>Caja</Typography>
          <Typography variant="body2" color="text.secondary">
            Turno actual, movimientos y arqueo
          </Typography>
        </Box>
        <TextField select size="small" label="Caja" sx={{ minWidth: 200 }}
                   value={cajaSeleccionadaId || ''}
                   onChange={(e) => setCajaSeleccionadaId(Number(e.target.value))}>
          {cajas.map(c => (
            <MenuItem key={c.cajaId} value={c.cajaId}>
              {c.nombre} {c.aperturaActiva ? '● activa' : ''}
            </MenuItem>
          ))}
        </TextField>
        <IconButton onClick={forzarRecarga}><RefreshIcon /></IconButton>
      </Stack>

      {turnoColgado && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<WarningIcon />}
               action={
                 <Button color="inherit" size="small"
                         onClick={() => setDialog({ tipo: 'cerrar', apertura, forzar: true })}>
                   Cerrar turno anterior
                 </Button>
               }>
          <b>Turno abierto desde {fmtFechaHora(apertura.fechaApertura)}.</b>
          {' '}Cierra el turno anterior antes de continuar operando o abrir uno nuevo.
        </Alert>
      )}

      {!apertura ? (
        <SinTurno caja={cajaSel} onAbrir={() => setDialog({ tipo: 'abrir', caja: cajaSel })} />
      ) : (
        <ConTurno caja={cajaSel} apertura={apertura} metodos={metodos}
                  onAbrirDialog={(d) => setDialog(d)}
                  forzarRecarga={forzarRecarga} />
      )}

      <HistorialTurnos cajaId={cajaSeleccionadaId} tick={tick} />

      {dialog?.tipo === 'abrir' && (
        <DialogAbrirTurno caja={dialog.caja}
                          onClose={() => setDialog(null)}
                          onDone={() => { setDialog(null); forzarRecarga(); notify('Turno abierto.', 'success'); }}
                          onError={(m) => notify(m, 'error')} />
      )}
      {dialog?.tipo === 'cerrar' && (
        <DialogCerrarTurno apertura={dialog.apertura} empresaId={empresaId}
                           onClose={() => setDialog(null)}
                           onDone={() => { setDialog(null); forzarRecarga(); notify('Turno cerrado.', 'success'); }}
                           onError={(m) => notify(m, 'error')} />
      )}
      {dialog?.tipo === 'movimiento' && (
        <DialogMovimiento apertura={dialog.apertura} metodos={metodos}
                          tipoInicial={dialog.tipoInicial}
                          onClose={() => setDialog(null)}
                          onDone={() => { setDialog(null); forzarRecarga(); notify('Movimiento registrado.', 'success'); }}
                          onError={(m) => notify(m, 'error')} />
      )}
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Sin turno activo
// ══════════════════════════════════════════════════════════════════════

function SinTurno({ caja, onAbrir }) {
  return (
    <Card sx={{ p: 4, textAlign: 'center' }}>
      <LockIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h5" gutterBottom>Caja cerrada</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        La caja <b>{caja.nombre}</b> no tiene turno abierto. Ábrelo para poder facturar.
      </Typography>
      <Button variant="contained" size="large" startIcon={<LockOpenIcon />}
              onClick={onAbrir}>
        Abrir turno
      </Button>
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Con turno activo — resumen + movimientos
// ══════════════════════════════════════════════════════════════════════

function ConTurno({ caja, apertura, metodos, onAbrirDialog, forzarRecarga }) {
  const [resumen, setResumen] = useState(null);
  const [movs, setMovs] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [r, m] = await Promise.all([
        apiCaja.resumen(apertura.aperturaCajaId),
        apiCaja.listarMovimientos(apertura.aperturaCajaId, { pageSize: 200 }),
      ]);
      setResumen(r);
      setMovs(m?.data || []);
    } catch { /* silent, el retry por refresh */ }
    finally { setCargando(false); }
  }, [apertura.aperturaCajaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const dur = useMemo(() => {
    const ini = new Date(apertura.fechaApertura);
    const ms = Date.now() - ini.getTime();
    const h = Math.floor(ms / 3600000);
    const min = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${min}m`;
  }, [apertura.fechaApertura]);

  return (
    <>
      {/* Header del turno */}
      <Card sx={{ p: 2, mb: 2, background: 'linear-gradient(90deg,#e8f5e9,#f1f8e9)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Typography variant="caption" color="text.secondary">Caja</Typography>
            <Typography variant="h6" fontWeight={700}>{caja.nombre}</Typography>
            <Chip size="small" label="ABIERTA" color="success" />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography variant="caption" color="text.secondary">Apertura</Typography>
            <Typography variant="body2">{fmtFechaHora(apertura.fechaApertura)}</Typography>
            <Typography variant="caption" color="text.secondary">Hace {dur}</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography variant="caption" color="text.secondary">Cajero</Typography>
            <Typography variant="body2">{apertura.nombreUsuario || '—'}</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography variant="caption" color="text.secondary">Fondo inicial</Typography>
            <Typography variant="body2" fontWeight={600}>{fmtCOP(apertura.montoInicial)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
              <Button size="small" variant="outlined" color="success" startIcon={<AddIcon />}
                      onClick={() => onAbrirDialog({ tipo: 'movimiento', apertura, tipoInicial: 'ENTRADA' })}>
                Entrada
              </Button>
              <Button size="small" variant="outlined" color="warning" startIcon={<RemoveIcon />}
                      onClick={() => onAbrirDialog({ tipo: 'movimiento', apertura, tipoInicial: 'SALIDA' })}>
                Salida
              </Button>
              <Button size="small" variant="contained" color="error" startIcon={<LockIcon />}
                      onClick={() => onAbrirDialog({ tipo: 'cerrar', apertura })}>
                Cerrar turno
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {/* Resumen por método de pago */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Ventas del turno por método de pago</Typography>
        {cargando ? (
          <LinearProgress />
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Total ventas</Typography>
                  <Typography variant="h6" fontWeight={700}>{fmtCOP(resumen?.totalVentas || 0)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Salidas / egresos</Typography>
                  <Typography variant="h6" fontWeight={700} color="warning.main">
                    {fmtCOP(resumen?.totalEgresos || 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Total esperado</Typography>
                  <Typography variant="h6" fontWeight={700} color="success.main">
                    {fmtCOP(resumen?.totalEsperado || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (inicial + ventas − egresos)
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Transacciones</Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {(resumen?.porMetodoPago || []).reduce((s, m) => s + m.transacciones, 0)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {(resumen?.porMetodoPago || []).length === 0 ? (
              <Alert severity="info">Sin ventas registradas en este turno todavía.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Método de pago</TableCell>
                      <TableCell align="right">Transacciones</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resumen.porMetodoPago.map((mp, i) => (
                      <TableRow key={i}>
                        <TableCell><Chip size="small" label={mp.metodoPago} /></TableCell>
                        <TableCell align="right">{mp.transacciones}</TableCell>
                        <TableCell align="right"><b>{fmtCOP(mp.total)}</b></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Card>

      {/* Movimientos del turno */}
      <Card>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">Movimientos del turno</Typography>
          <Typography variant="caption" color="text.secondary">
            {movs.length} movimiento{movs.length === 1 ? '' : 's'} registrado{movs.length === 1 ? '' : 's'}
          </Typography>
        </Box>
        {movs.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>Sin movimientos manuales.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Concepto</TableCell>
                  <TableCell>Método</TableCell>
                  <TableCell align="right">Monto</TableCell>
                  <TableCell>Ref.</TableCell>
                  <TableCell>Notas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movs.map(m => {
                  const met = metodos.find(x => x.metodoPagoId === m.metodoPagoId);
                  return (
                    <TableRow key={m.movimientoCajaId}>
                      <TableCell><Typography variant="caption">{fmtFechaHora(m.fechaMovimiento)}</Typography></TableCell>
                      <TableCell>
                        <Chip size="small" label={m.tipoMovimiento}
                              color={m.tipoMovimiento === 'ENTRADA' ? 'success' :
                                     m.tipoMovimiento === 'SALIDA' ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell><Typography variant="body2">{m.concepto || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{met?.nombre || `#${m.metodoPagoId}`}</Typography></TableCell>
                      <TableCell align="right"><b>{fmtCOP(m.monto)}</b></TableCell>
                      <TableCell><Typography variant="caption">{m.referencia || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{m.notas || ''}</Typography></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Abrir turno
// ══════════════════════════════════════════════════════════════════════

function DialogAbrirTurno({ caja, onClose, onDone, onError }) {
  const [monto, setMonto] = useState(String(caja.fondoInicial || 0));
  const [notas, setNotas] = useState('');
  const [g, setG] = useState(false);
  const submit = async () => {
    setG(true);
    try {
      await apiCaja.abrir({
        cajaId: caja.cajaId, montoInicial: Number(monto) || 0,
        notas: notas || null,
      });
      onDone();
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo abrir el turno.'); }
    finally { setG(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Abrir turno · {caja.nombre}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          El monto inicial es el efectivo con que arrancas la caja hoy.
        </Alert>
        <TextField fullWidth autoFocus required type="number" label="Monto inicial en efectivo"
                   value={monto} onChange={(e) => setMonto(e.target.value)}
                   InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                   sx={{ mb: 2 }} />
        <TextField fullWidth multiline rows={2} label="Notas (opcional)"
                   value={notas} onChange={(e) => setNotas(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" color="success" onClick={submit} disabled={g}>
          {g ? 'Abriendo…' : 'Abrir turno'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Cerrar turno (arqueo)
// ══════════════════════════════════════════════════════════════════════

function DialogCerrarTurno({ apertura, empresaId, onClose, onDone, onError }) {
  const [resumen, setResumen] = useState(null);
  const [montoReal, setMontoReal] = useState('');
  const [notas, setNotas] = useState('');
  const [cargando, setCargando] = useState(true);
  const [g, setG] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await apiCaja.resumen(apertura.aperturaCajaId);
        setResumen(r);
        setMontoReal(String(r.totalEsperado || 0));
      } catch (e) { onError(e.mensajeUsuario || 'No se pudo cargar el resumen.'); onClose(); }
      finally { setCargando(false); }
    })();
  }, [apertura.aperturaCajaId, onClose, onError]);

  const submit = async () => {
    if (montoReal === '' || isNaN(Number(montoReal))) return;
    setG(true);
    try {
      await apiCaja.cerrar({
        aperturaCajaId: apertura.aperturaCajaId,
        montoFinalReal: Number(montoReal),
        notas: notas || null,
      });
      onDone();
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo cerrar el turno.'); }
    finally { setG(false); }
  };

  const esperado = resumen?.totalEsperado || 0;
  const diferencia = Number(montoReal || 0) - esperado;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Cerrar turno · Arqueo</DialogTitle>
      <DialogContent dividers>
        {cargando || !resumen ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Cuenta el efectivo real en la caja y captúralo abajo.
              La diferencia entre lo esperado por el sistema y lo contado quedará registrada.
            </Alert>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Fondo inicial</Typography>
                <Typography variant="body1">{fmtCOP(resumen.montoInicial)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Total ventas</Typography>
                <Typography variant="body1">{fmtCOP(resumen.totalVentas)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Salidas / egresos</Typography>
                <Typography variant="body1" color="warning.main">
                  −{fmtCOP(resumen.totalEgresos)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Total esperado</Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  {fmtCOP(esperado)}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            <TextField fullWidth required autoFocus type="number"
                       label="Monto real contado en caja"
                       value={montoReal} onChange={(e) => setMontoReal(e.target.value)}
                       InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                       sx={{ mb: 2 }} />

            {montoReal !== '' && (
              <Alert severity={diferencia === 0 ? 'success' : diferencia > 0 ? 'info' : 'error'} sx={{ mb: 2 }}>
                <b>Diferencia: {diferencia >= 0 ? '+' : ''}{fmtCOP(diferencia)}</b><br />
                {diferencia === 0 && 'Caja cuadrada. ✓'}
                {diferencia > 0 && 'Hay más efectivo del esperado (sobrante).'}
                {diferencia < 0 && 'Falta efectivo respecto a lo esperado (faltante).'}
              </Alert>
            )}

            <TextField fullWidth multiline rows={2} label="Notas del cierre (opcional)"
                       value={notas} onChange={(e) => setNotas(e.target.value)} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" color="error" onClick={submit}
                disabled={cargando || montoReal === '' || g}>
          {g ? 'Cerrando…' : 'Cerrar turno'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Movimiento manual (ENTRADA o SALIDA)
// ══════════════════════════════════════════════════════════════════════

function DialogMovimiento({ apertura, metodos, tipoInicial, onClose, onDone, onError }) {
  const efectivo = metodos.find(m => m.tipoPago === 'EFECTIVO');
  const [f, setF] = useState({
    tipoMovimiento: tipoInicial || 'ENTRADA',
    metodoPagoId: efectivo?.metodoPagoId || (metodos[0]?.metodoPagoId || ''),
    concepto: '', monto: '', referencia: '', notas: '',
    codigoAutorizador: '',   // solo para SALIDA
  });
  const [g, setG] = useState(false);
  const [autorizando, setAutorizando] = useState(false);

  const esSalida = f.tipoMovimiento === 'SALIDA';

  const submit = async () => {
    if (!f.concepto.trim() || !f.monto || Number(f.monto) <= 0) return;

    setG(true);
    try {
      // Autorizar salida con badge si aplica
      if (esSalida) {
        if (!f.codigoAutorizador.trim()) {
          onError('Para movimientos de salida se requiere autorización de supervisor.');
          setG(false); return;
        }
        setAutorizando(true);
        try {
          await apiCaja.autorizarAccion({
            codigoBarras: f.codigoAutorizador.trim(),
            funcionalidadCodigo: 'CAJA_MOVIMIENTO_SALIDA',
            empresaId: 0, // el backend resuelve desde la apertura
            entidad: 'MovimientoCaja',
          });
        } catch (e) {
          onError(e.mensajeUsuario || 'Autorización inválida. Verifica el badge del supervisor.');
          setG(false); setAutorizando(false); return;
        }
        setAutorizando(false);
      }

      await apiCaja.registrarMovimiento({
        aperturaCajaId: apertura.aperturaCajaId,
        metodoPagoId: Number(f.metodoPagoId),
        tipoMovimiento: f.tipoMovimiento,
        concepto: f.concepto.trim(),
        monto: Number(f.monto),
        referencia: f.referencia || null,
        notas: f.notas || null,
      });
      onDone();
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo registrar.'); }
    finally { setG(false); }
  };

  const ok = f.concepto.trim() && f.monto && Number(f.monto) > 0
    && f.metodoPagoId && (!esSalida || f.codigoAutorizador.trim());

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {f.tipoMovimiento === 'ENTRADA' ? 'Registrar entrada' : 'Registrar salida'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label="Tipo de movimiento"
                     value={f.tipoMovimiento}
                     onChange={(e) => setF(s => ({ ...s, tipoMovimiento: e.target.value }))}>
            <MenuItem value="ENTRADA">Entrada (ingreso extra)</MenuItem>
            <MenuItem value="SALIDA">Salida (gasto, retiro)</MenuItem>
          </TextField>
          <TextField select label="Método"
                     value={f.metodoPagoId}
                     onChange={(e) => setF(s => ({ ...s, metodoPagoId: e.target.value }))}>
            {metodos.map(m => (
              <MenuItem key={m.metodoPagoId} value={m.metodoPagoId}>{m.nombre}</MenuItem>
            ))}
          </TextField>
          <TextField required autoFocus label="Concepto"
                     placeholder={esSalida ? 'Pago proveedor, gasto…' : 'Ingreso extra, cambio…'}
                     value={f.concepto}
                     onChange={(e) => setF(s => ({ ...s, concepto: e.target.value }))} />
          <TextField required type="number" label="Monto"
                     value={f.monto} onChange={(e) => setF(s => ({ ...s, monto: e.target.value }))}
                     InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
          <TextField label="Referencia (opcional)"
                     value={f.referencia}
                     onChange={(e) => setF(s => ({ ...s, referencia: e.target.value }))} />
          <TextField label="Notas (opcional)" multiline rows={2}
                     value={f.notas} onChange={(e) => setF(s => ({ ...s, notas: e.target.value }))} />

          {esSalida && (
            <>
              <Divider>Autorización</Divider>
              <Alert severity="warning" icon={<WarningIcon />}>
                Los movimientos de salida requieren autorización de supervisor.
                Escanea o ingresa el código de badge del supervisor.
              </Alert>
              <TextField required label="Código del supervisor"
                         value={f.codigoAutorizador}
                         onChange={(e) => setF(s => ({ ...s, codigoAutorizador: e.target.value }))}
                         autoComplete="off" />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" color={esSalida ? 'warning' : 'success'}
                onClick={submit} disabled={!ok || g}>
          {g ? (autorizando ? 'Autorizando…' : 'Registrando…') : 'Registrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Historial de turnos
// ══════════════════════════════════════════════════════════════════════

function HistorialTurnos({ cajaId, tick }) {
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(false);
  useEffect(() => {
    if (!cajaId) return;
    (async () => {
      setCargando(true);
      try {
        const r = await apiCaja.historialAperturas(cajaId, { pageSize: 20 });
        setDatos(Array.isArray(r) ? r : []);
      } catch { /* silent */ }
      finally { setCargando(false); }
    })();
  }, [cajaId, tick]);

  if (!cajaId) return null;

  return (
    <Card sx={{ mt: 2 }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
        <HistoryIcon sx={{ mr: 1 }} />
        <Typography variant="h6">Historial de turnos</Typography>
      </Box>
      {cargando ? (
        <LinearProgress />
      ) : datos.length === 0 ? (
        <Alert severity="info" sx={{ m: 2 }}>Sin turnos previos.</Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Apertura</TableCell>
                <TableCell>Cierre</TableCell>
                <TableCell>Cajero</TableCell>
                <TableCell align="right">Inicial</TableCell>
                <TableCell align="right">Esperado</TableCell>
                <TableCell align="right">Real</TableCell>
                <TableCell align="right">Diferencia</TableCell>
                <TableCell>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {datos.map(a => (
                <TableRow key={a.aperturaCajaId}>
                  <TableCell><Typography variant="caption">{fmtFechaHora(a.fechaApertura)}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{fmtFechaHora(a.fechaCierre)}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{a.nombreApertura || '—'}</Typography></TableCell>
                  <TableCell align="right">{fmtCOP(a.montoInicial)}</TableCell>
                  <TableCell align="right">{a.montoFinalSistema != null ? fmtCOP(a.montoFinalSistema) : '—'}</TableCell>
                  <TableCell align="right">{a.montoFinalReal != null ? fmtCOP(a.montoFinalReal) : '—'}</TableCell>
                  <TableCell align="right">
                    {a.diferencia != null ? (
                      <b style={{ color: a.diferencia === 0 ? '#2e7d32'
                                          : a.diferencia > 0 ? '#0288d1' : '#d32f2f' }}>
                        {a.diferencia > 0 ? '+' : ''}{fmtCOP(a.diferencia)}
                      </b>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={a.estado}
                          color={a.estado === 'ABIERTA' ? 'success' : 'default'} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
}

export default Caja;
